import { IProject } from "../views/project/types/project"

/**
 * Apply a service-provider project's rebate percentage to an amount.
 *
 * DELIBERATELY NOT EXPORTED (issue #120). The rebate is a discount on the study TOTAL and must
 * never be applied to an individual line item — an invoice, a milestone or a payment. Exporting a
 * generic "discount any amount" helper is exactly how this went wrong twice: the accounting table
 * discounted every invoice row (so the table disagreed with the invoice modal), and the
 * "% of Total" column discounted its numerator as well as its denominator (so the rebate cancelled
 * out and the column could never move).
 *
 * Callers get calculateProjectOfferTotalWithRebate() instead, which can only ever produce the one
 * figure that is legitimately rebated.
 *
 * @param amount - the gross amount
 * @param project - the project whose `rebate` percentage to apply
 * @returns the amount after rebate; unchanged when no rebate is set
 */
const applyProjectRebate = (amount: number, project: IProject): number => {
  const projectRebate = project.rebate ? +project.rebate : 0
  return projectRebate ? amount * (1 - (projectRebate / 100)) : amount
}

/**
 * The study's total contract value after the rebate — the only rebated figure in the app.
 *
 * This is the denominator behind the Invoice Completion widget and the accounting page's
 * "% of Total" column. `financial_calculation.total_budget` is a direct-costs-only total (it sums
 * ProjectOfferDirectCost), which is why the rebate applies to the whole of it.
 *
 * @param project - the project to price
 * @returns the rebated study total, or 0 when the project has no calculated total yet
 * @example
 * // total_budget 373961, rebate 20
 * calculateProjectOfferTotalWithRebate(project) // 299168.80
 */
export const calculateProjectOfferTotalWithRebate = (project: IProject) => {
  /*
   * `?? 0` covers an EMPTY financial_calculation, not a missing one, and that distinction is the
   * whole contract here. Every project the store can hold carries the object — `_initialProject`
   * sets `financial_calculation: {}` — so the total is merely absent until the figures load, and
   * zero is the honest reading of that.
   *
   * A project WITHOUT the object is not a state this app produces; it only arrives from a
   * `{} as IProject` cast, which is a bug at the call site. Let it throw. Adding `?.` here would
   * silence it and quietly report 0% for every invoice in the accounting table AND in the Invoice
   * Completion widget, which reads the same figure — a wrong number a client can act on, in place
   * of an error a developer can see (issue #20 review, claim 2).
   */
  return applyProjectRebate(project.financial_calculation.total_budget ?? 0, project)
}
